import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Clock, CheckCircle, XCircle, Truck, ChefHat, Package, AlertCircle, Calendar } from 'lucide-react';
import ConfirmDialog from '../components/ConfirmDialog';
import apiService from '../services/api';
import { useCommandesWebSocket } from '../hooks/useCommandesWebSocket';
import { CommandeToastManager } from '../components/CommandeToast';
import audioService from '../services/audioService';
import { getNotificationEventKey } from '../utils/commandeToastNotificationId';
import {
  isCommandeToastDismissed,
  markCommandeToastDismissed,
} from '../utils/commandeToastDismissSession';
import { isCommandeEnCours, isCommandeLivreurModifiable } from '../utils/commandeStatuts';
import { getRdvPaymentView, formatEuro } from '../utils/rdvPaymentDisplay';
import RdvTerminerModal from '../components/rendezVous/RdvTerminerModal';
import { createCaisseIdempotencyKey } from '../utils/caisseIdempotency';
import {
  resolveLivraisonAdminBadgeMeta,
  resolveLivraisonCommandeDisplay,
} from '../utils/livraisonCommandeDisplay';
import PlatformCommandesMaquetteLists from '../components/mesCommandes/PlatformCommandesMaquetteLists';
import MesCommandesListPaiementCell from '../components/mesCommandes/MesCommandesListPaiementCell';
import MesCommandesOrderDetailModal from '../components/mesCommandes/MesCommandesOrderDetailModal';
import MesCommandesRdvDetailModal from '../components/mesCommandes/MesCommandesRdvDetailModal';
import { getCommandeFinancier, getListPrincipalAmount } from '../utils/commandeFinanceDisplay';
import {
  isCommandePaiementEnLigne,
  getCommandeSoldeRemboursable,
  hasStripePaymentIntent,
  parseSavReference,
} from '../utils/mesCommandesOrderDetailFinance';
import { useCommandePoidsAjustement } from '../hooks/useCommandePoidsAjustement';
import { formatCommandeStatutUpdateError } from '../utils/commandeStatutUpdateErrors';

const DEFAULT_PAGE_SIZE = 100;

const Commandes = () => {
  const [commandes, setCommandes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedCommande, setSelectedCommande] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [updatingStatusId, setUpdatingStatusId] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, pageSize: DEFAULT_PAGE_SIZE, total: 0 });
  const [confirmation, setConfirmation] = useState(null);
  const [terminerModal, setTerminerModal] = useState({ open: false, commande: null });
  const [encaisserSoldeUi, setEncaisserSoldeUi] = useState({
    loading: false,
    methode: 'CARTE',
    hasSession: false,
    probing: false,
    error: '',
  });
  const [livreursDisponibles, setLivreursDisponibles] = useState([]);
  const [livreurHistorique, setLivreurHistorique] = useState(null);
  const [selectedLivreurId, setSelectedLivreurId] = useState('');
  const [assigningLivreur, setAssigningLivreur] = useState(false);
  const [reprintLoading, setReprintLoading] = useState(false);
  const [factureLoading, setFactureLoading] = useState(false);
  const [avoirLoadingId, setAvoirLoadingId] = useState(null);
  const [refundLoading] = useState(false);
  const [indisponibleMessage] = useState('');
  const [markingIndisponible] = useState(false);
  const expeditionCapabilities = useMemo(() => ({ hasBoxtal: false }), []);
  const expeditionLabelUi = useMemo(() => ({ loading: false, downloading: false, status: null, format: null }), []);

  const [searchInput, setSearchInput] = useState('');
  const [deepLinkCommandeId, setDeepLinkCommandeId] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    statut: '',
    modeRetrait: '',
    dateMin: '',
    dateMax: '',
    type: ''
  });
  const [newCommandes, setNewCommandes] = useState([]); // Pour suivre les nouvelles commandes
  const [notifications, setNotifications] = useState([]); // Pour les toasts

  const appendCommandeToastNotification = useCallback((type, data) => {
    const notification = { type, data, timestamp: Date.now() };
    const eventKey = getNotificationEventKey(notification);
    if (isCommandeToastDismissed(eventKey)) {
      return;
    }
    setNotifications((prev) => {
      if (prev.some((entry) => getNotificationEventKey(entry) === eventKey)) {
        return prev;
      }
      return [...prev, notification];
    });
  }, []);
  const [sectionsExpanded, setSectionsExpanded] = useState({
    enCours: true, // Toujours ouvert
    terminees: false,
    annulees: false
  });

  const paramsRef = useRef({ page: 1, pageSize: DEFAULT_PAGE_SIZE });
  const filtersInitializedRef = useRef(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Récupérer l'utilisateur actuel
  const getCurrentUser = () => {
    try {
      const userData = localStorage.getItem('admin_user');
      if (userData) {
        return JSON.parse(userData);
      }
    } catch (error) {
      console.error('Erreur parsing user data:', error);
    }
    return null;
  };

  // Hook WebSocket pour les notifications
  const currentUser = getCurrentUser();
  const isCommercant = currentUser?.role === 'COMMERCANT';
  const userCommercantId = currentUser?.commercantId || currentUser?.commercant?.id || null;

  const { connected, lastNotification } = useCommandesWebSocket(
    isCommercant ? userCommercantId : null,
    true,
    (data) => {
      console.log('[Commandes] Nouvelle commande WebSocket:', data);

      const commandeId = data.commandeId || data.id;
      setNewCommandes(prev => [...prev, commandeId]);

      setTimeout(() => {
        setNewCommandes(prev => prev.filter(id => id !== commandeId));
      }, 10000);

      appendCommandeToastNotification('commande', data);

      if (location.pathname === '/commandes') {
        setTimeout(() => {
          fetchCommandes({ silent: true });
        }, 500);
      }
    },
    (data) => {
      console.log('[Commandes] Nouveau rendez-vous WebSocket:', data);

      appendCommandeToastNotification('rendezvous', data);

      if (location.pathname === '/commandes') {
        setTimeout(() => {
          fetchCommandes({ silent: true });
        }, 500);
      }
    },
    false
  );

  const fetchCommandes = useCallback(async (overrides = {}) => {
    if (!apiService.isAuthenticated()) {
      setCommandes([]);
      setError('Session expirée. Veuillez vous reconnecter.');
      setLoading(false);
      return;
    }

    try {
      if (overrides.silent !== true) {
        setLoading(true);
      }
      const finalParams = { ...paramsRef.current, ...overrides };
      paramsRef.current = finalParams;

      const queryParams = { ...finalParams };
      delete queryParams.silent;
      
      // Pour les commerçants, forcer le filtre commercantId
      const currentUser = getCurrentUser();
      const isCommercant = currentUser?.role === 'COMMERCANT';
      const userCommercantId = currentUser?.commercantId || currentUser?.commercant?.id || null;
      
      if (isCommercant) {
        if (userCommercantId) {
          queryParams.commercantId = userCommercantId;
        } else {
          // Si COMMERCANT mais pas de commercantId, essayer de le récupérer via verifyToken
          console.warn('[Commandes] COMMERCANT sans commercantId, tentative de récupération...');
          try {
            const userData = await apiService.verifyToken();
            const fetchedCommercantId = userData?.user?.commercantId || userData?.data?.user?.commercantId || userData?.commercantId || null;
            if (fetchedCommercantId) {
              queryParams.commercantId = fetchedCommercantId;
              console.log('[Commandes] commercantId récupéré:', fetchedCommercantId);
            } else {
              throw new Error('Impossible de récupérer votre boutique. Veuillez contacter le support.');
            }
          } catch (tokenError) {
            console.error('[Commandes] Erreur récupération commercantId:', tokenError);
            throw new Error('Impossible de récupérer votre boutique. Veuillez contacter le support.');
          }
        }
      }
      
      if (filters.search.trim()) {
        queryParams.search = filters.search.trim();
      } else {
        delete queryParams.search;
      }
      if (filters.statut) {
        queryParams.statut = filters.statut;
      } else {
        delete queryParams.statut;
      }
      if (filters.modeRetrait) {
        queryParams.modeRetrait = filters.modeRetrait;
      } else {
        delete queryParams.modeRetrait;
      }
      if (filters.dateMin) {
        queryParams.dateMin = filters.dateMin;
      } else {
        delete queryParams.dateMin;
      }
      if (filters.dateMax) {
        queryParams.dateMax = filters.dateMax;
      } else {
        delete queryParams.dateMax;
      }
      if (filters.type) {
        queryParams.type = filters.type;
        if (filters.type === 'RENDEZ_VOUS') {
          delete queryParams.modeRetrait;
        }
      } else {
        delete queryParams.type;
      }

      const data = await apiService.getCommandes(queryParams);
      paramsRef.current = {
        page: data.page || finalParams.page,
        pageSize: data.pageSize || finalParams.pageSize,
      };
      setPagination({
        page: paramsRef.current.page,
        pageSize: paramsRef.current.pageSize,
        total: data.total || 0,
      });
      setCommandes(data.data || []);
      setError('');
    } catch (err) {
      console.error('❌ [Commandes] Erreur détaillée:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
        stack: err.stack
      });
      
      if (err?.message === 'NOT_AUTHENTICATED' || err?.response?.status === 401) {
        setError('Session expirée. Veuillez vous reconnecter.');
        setCommandes([]);
        return;
      }

      setCommandes((prev) => {
        if (prev.length === 0) {
          const errorMessage = err.response?.data?.error || err.response?.data?.message || err.message || 'Erreur inconnue';
          setError(`Erreur lors du chargement des commandes: ${errorMessage}`);
        }
        return prev;
      });
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    if (!apiService.isAuthenticated()) {
      apiService.logoutHard();
      return;
    }

    fetchCommandes();
  }, [fetchCommandes]);

  useEffect(() => {
    if (!apiService.isAuthenticated()) return undefined;

    // Polling adaptatif : sanity check 60s si WS connecté, sinon 5s
    const interval = connected ? 60000 : 5000;
    const intervalId = setInterval(() => {
      fetchCommandes({ silent: true });
    }, interval);

    return () => clearInterval(intervalId);
  }, [fetchCommandes, connected]);

  useEffect(() => {
    if (!filtersInitializedRef.current) {
      filtersInitializedRef.current = true;
      return;
    }
    paramsRef.current.page = 1;
    fetchCommandes({ page: 1 });
  }, [filters, fetchCommandes]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setFilters((prev) => {
        if (prev.search === searchInput.trim()) {
          return prev;
        }
        return { ...prev, search: searchInput.trim() };
      });
    }, 300);

    return () => clearTimeout(handler);
  }, [searchInput]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const targetCommandeId = params.get('commandeId');
    if (targetCommandeId) {
      setDeepLinkCommandeId(targetCommandeId);
    }
  }, [location.search]);

  useEffect(() => {
    if (!deepLinkCommandeId) return;

    const existingCommande = commandes.find(
      (cmd) => cmd.id === deepLinkCommandeId || cmd.numero === deepLinkCommandeId
    );

    if (existingCommande) {
      // Arrêter la répétition du son quand l'utilisateur ouvre une commande
      audioService.stopRepeat();
      setSelectedCommande(existingCommande);
      setShowDetailModal(true);
      setDeepLinkCommandeId(null);
      return;
    }

    let cancelled = false;
    const fetchCommandeDetail = async () => {
      try {
        const commandeDetail = await apiService.getCommande(deepLinkCommandeId);
        if (!cancelled && commandeDetail) {
          // Arrêter la répétition du son quand l'utilisateur ouvre une commande
          audioService.stopRepeat();
          setSelectedCommande(commandeDetail);
          setShowDetailModal(true);
        }
      } catch (detailError) {
        console.error('Erreur lors du chargement de la commande ciblée :', detailError);
      } finally {
        if (!cancelled) {
          setDeepLinkCommandeId(null);
        }
      }
    };

    fetchCommandeDetail();

    return () => {
      cancelled = true;
    };
  }, [deepLinkCommandeId, commandes]);

  const handleSelectFilter = (key, value) => {
    setFilters((prev) => {
      const next = { ...prev, [key]: value };

      if (key === 'type') {
        next.statut = '';
        if (value === 'RENDEZ_VOUS') {
          next.modeRetrait = '';
        }
      }

      return next;
    });
  };

  const handleDateFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const resetFilters = () => {
    setSearchInput('');
    setFilters({
      search: '',
      statut: '',
      modeRetrait: '',
      dateMin: '',
      dateMax: '',
      type: ''
    });
  };

  const isRendezVous = (commande) => commande?.type === 'RENDEZ_VOUS';
  const isPaiementStripe = (cmd) =>
    isCommandePaiementEnLigne(cmd) || cmd?.methodePaiement === 'CARTE';

  const refreshCommandeDetail = useCallback(async (commandeId) => {
    if (!commandeId) return;
    try {
      const data = await apiService.getCommande(commandeId);
      const full = data?.commande || data;
      if (full?.id) {
        setSelectedCommande((prev) => (prev?.id === full.id ? { ...prev, ...full } : full));
      }
    } catch (err) {
      console.error('Erreur refresh commande:', err);
    }
  }, []);

  const refreshCommandesAfterPoidsSave = useCallback(async () => {
    await fetchCommandes({ silent: true });
  }, [fetchCommandes]);

  const {
    poidsDrafts,
    setPoidsDrafts,
    poidsComplementUrl,
    poidsError,
    poidsMessage,
    savingPoids,
    handleSavePoidsAjustement,
  } = useCommandePoidsAjustement({
    selectedCommande,
    setSelectedCommande,
    refreshCommandeDetail,
    onAfterSave: refreshCommandesAfterPoidsSave,
    formatEuro,
  });

  const openCommandeDetail = useCallback(async (commande) => {
    if (!commande?.id) return;
    audioService.stopRepeat();
    setSelectedCommande(commande);
    setShowDetailModal(true);
    await refreshCommandeDetail(commande.id);
  }, [refreshCommandeDetail]);

  const closeDetailModal = () => {
    setShowDetailModal(false);
    setReprintLoading(false);
  };

  const resolveCommercantIdForCommande = (commande) =>
    commande?.commercantId || commande?.commercant?.id || null;

  const normalizeLivreursList = (data) => {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.livreurs)) return data.livreurs;
    return [];
  };

  const loadLivreursForCommande = async (commande) => {
    const targetCommercantId = resolveCommercantIdForCommande(commande);
    if (!targetCommercantId) return;
    try {
      const data = await apiService.getLivreurs(targetCommercantId);
      const list = normalizeLivreursList(data).filter((l) => l.actif !== false);
      setLivreursDisponibles(list);
      setSelectedLivreurId(commande?.livreurId || commande?.livreur?.id || '');
    } catch (err) {
      console.error('Erreur chargement livreurs:', err);
      setLivreursDisponibles([]);
    }
  };

  const handleAssignerLivreur = async () => {
    if (!selectedCommande || !selectedLivreurId) return;
    if (!isCommandeLivreurModifiable(selectedCommande.statut)) {
      alert('Cette commande est terminée ou annulée : le livreur ne peut plus être modifié.');
      return;
    }
    try {
      setAssigningLivreur(true);
      await apiService.assignerLivreur(selectedCommande.id, { livreurId: selectedLivreurId });
      await refreshCommandeDetail(selectedCommande.id);
      await fetchCommandes({ silent: true });
    } catch (err) {
      alert(err?.message || "Erreur lors de l'assignation du livreur");
    } finally {
      setAssigningLivreur(false);
    }
  };

  useEffect(() => {
    if (!showDetailModal || !selectedCommande?.id) {
      setLivreurHistorique(null);
      return;
    }
    if (selectedCommande.modeRetrait !== 'livraison' || isRendezVous(selectedCommande)) {
      setLivreurHistorique(null);
      return;
    }
    refreshCommandeDetail(selectedCommande.id);
  }, [showDetailModal, selectedCommande?.id, refreshCommandeDetail]);

  useEffect(() => {
    if (!showDetailModal || !selectedCommande) return;
    if (
      selectedCommande.modeRetrait === 'livraison' &&
      !isRendezVous(selectedCommande) &&
      isCommandeLivreurModifiable(selectedCommande.statut)
    ) {
      loadLivreursForCommande(selectedCommande);
    } else {
      setLivreursDisponibles([]);
      setSelectedLivreurId('');
    }
  }, [showDetailModal, selectedCommande?.id, selectedCommande?.modeRetrait, selectedCommande?.livreurId, selectedCommande?.statut]);

  useEffect(() => {
    if (!showDetailModal || !selectedCommande?.id) {
      setLivreurHistorique(null);
      return;
    }
    const livreurId = selectedCommande.livreurId || selectedCommande.livreur?.id;
    if (!livreurId || selectedCommande.livreur) {
      setLivreurHistorique(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await apiService.getLivreur(livreurId);
        const livreur = data?.livreur || data;
        if (!cancelled && livreur?.id) setLivreurHistorique(livreur);
      } catch (err) {
        console.error('Erreur chargement livreur:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showDetailModal, selectedCommande?.id, selectedCommande?.livreurId, selectedCommande?.livreur]);

  const handleReprintTicket = async () => {
    if (!selectedCommande?.id || reprintLoading) return;
    setReprintLoading(true);
    try {
      await apiService.reprintTicket(selectedCommande.id);
    } catch (err) {
      alert(err?.message || 'Erreur d\'impression');
    } finally {
      setReprintLoading(false);
    }
  };

  const handleDownloadFacture = async () => {
    if (!selectedCommande) return;
    setFactureLoading(true);
    try {
      await apiService.downloadCommandeFacture(selectedCommande.id);
    } catch (err) {
      alert(err?.message || 'Erreur téléchargement facture');
    } finally {
      setFactureLoading(false);
    }
  };

  const handleDownloadAvoir = async (avoirId) => {
    if (!selectedCommande?.id || !avoirId) return;
    setAvoirLoadingId(avoirId);
    try {
      await apiService.downloadCommandeAvoir(selectedCommande.id, avoirId);
    } catch (err) {
      alert(err?.message || 'Erreur téléchargement avoir');
    } finally {
      setAvoirLoadingId(null);
    }
  };

  const openRefundModal = () => {
    alert(
      'Remboursement : utilisez Mes commandes commerçant (assistance) ou le parcours Stripe adapté au commerçant.',
    );
  };

  const STATUTS_LIGNE_INDISPONIBLE = ['EN_ATTENTE', 'CONFIRMEE', 'EN_PREPARATION'];
  const noop = () => {};

  const getRdvParticipants = (commande) => Math.max(1, Number(commande?.participants ?? 1));

  const getRdvTotalAmount = (commande) =>
    Number(commande?.prixFinal ?? commande?.total ?? commande?.totalTTC ?? 0);

  const getRdvUnitPrice = (commande) => {
    const participants = getRdvParticipants(commande);
    const total = getRdvTotalAmount(commande);
    if (!participants || participants <= 0) {
      return total;
    }
    return total / participants;
  };

  const formatParticipantsLabel = (count) =>
    `${count} participant${count > 1 ? 's' : ''}`;

  const RDV_STATUS_CONFIG = {
    EN_ATTENTE: {
      label: 'En attente',
      color: 'bg-yellow-100 text-yellow-800',
      icon: <Clock className="h-4 w-4" />,
    },
    CONFIRME: {
      label: 'Confirmé',
      color: 'bg-blue-100 text-blue-800',
      icon: <CheckCircle className="h-4 w-4" />,
    },
    TERMINE: {
      label: 'Terminé',
      color: 'bg-green-100 text-green-800',
      icon: <CheckCircle className="h-4 w-4" />,
    },
    ABSENT: {
      label: 'Client absent',
      color: 'bg-orange-100 text-orange-800',
      icon: <AlertCircle className="h-4 w-4" />,
    },
    ANNULE: {
      label: 'Annulé',
      color: 'bg-red-100 text-red-800',
      icon: <XCircle className="h-4 w-4" />,
    },
  };

  const getRdvStatusDisplay = (statut) =>
    RDV_STATUS_CONFIG[statut] || {
      label: statut,
      color: 'bg-gray-100 text-gray-800',
      icon: <Clock className="h-4 w-4" />,
    };

  const RDV_ACTIONS = {
    EN_ATTENTE: [
      { status: 'CONFIRME', label: 'Confirmer', tone: 'primary' },
      { status: 'ANNULE', label: 'Annuler', tone: 'danger' },
    ],
    CONFIRME: [
      { status: 'TERMINE', label: 'Terminer', tone: 'primary' },
      { status: 'ABSENT', label: 'Client absent', tone: 'primary' },
      { status: 'ANNULE', label: 'Annuler', tone: 'danger' },
    ],
    TERMINE: [],
    ABSENT: [],
    ANNULE: [],
  };

  const getRdvActions = (statut) => RDV_ACTIONS[statut] || [];

  const getStatusDisplay = (commande) => {
    if (isRendezVous(commande)) {
      return getRdvStatusDisplay(commande.statut);
    }

    return {
      label: getStatusLabel(commande.statut, commande.modeRetrait),
      color: getStatusColor(commande.statut),
      icon: getStatusIcon(commande.statut),
    };
  };

  const formatDateTime = (value) => {
    if (!value) return '';
    return new Date(value).toLocaleString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const renderTypeBadge = (typeBadge) => (
    <>
      <span
        className={`mt-1 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${typeBadge.className}`}
      >
        {typeBadge.icon}
        {typeBadge.label}
      </span>
      {typeBadge.timingLines?.length ? (
        <span
          className={`mt-1 text-[11px] leading-tight whitespace-pre-line ${
            typeBadge.kind === 'programmee' ? 'font-semibold text-amber-800' : 'text-gray-500'
          }`}
        >
          {typeBadge.timingLines.join('\n')}
        </span>
      ) : null}
    </>
  );

  const getTypeBadge = (commande) => {
    if (isRendezVous(commande)) {
      return {
        label: 'Rendez-vous',
        className: 'bg-purple-100 text-purple-800',
        icon: <Calendar className="h-3 w-3" />,
      };
    }

    if (commande.modeRetrait === 'livraison') {
      const livraisonBadge = resolveLivraisonAdminBadgeMeta(commande);
      return {
        label: livraisonBadge?.label || 'Livraison',
        className: livraisonBadge?.className || 'bg-green-100 text-green-800',
        icon: livraisonBadge?.kind === 'programmee'
          ? <Clock className="h-3 w-3" />
          : <Truck className="h-3 w-3" />,
        kind: livraisonBadge?.kind || 'asap',
        timingLines: livraisonBadge?.timingLines || ['Dès que possible'],
      };
    }

    if (commande.modeRetrait === 'clickandcollect') {
      return {
        label: 'Click & Collect',
        className: 'bg-blue-100 text-blue-800',
        icon: <Package className="h-3 w-3" />,
      };
    }

    return {
      label: 'Commande',
      className: 'bg-gray-100 text-gray-700',
      icon: <ChefHat className="h-3 w-3" />,
    };
  };

  const getRdvActionIcon = (status) => {
    switch (status) {
      case 'CONFIRME':
        return { icon: <CheckCircle className="h-5 w-5" />, className: 'text-blue-600 hover:text-blue-900' };
      case 'TERMINE':
        return { icon: <CheckCircle className="h-5 w-5" />, className: 'text-green-600 hover:text-green-900' };
      case 'ABSENT':
        return { icon: <AlertCircle className="h-5 w-5" />, className: 'text-orange-600 hover:text-orange-900' };
      case 'ANNULE':
        return { icon: <XCircle className="h-5 w-5" />, className: 'text-red-600 hover:text-red-900' };
      default:
        return { icon: <Clock className="h-5 w-5" />, className: 'text-gray-500 hover:text-gray-700' };
    }
  };

  const getRdvActionButtonClass = (status) => {
    switch (status) {
      case 'CONFIRME':
        return 'bg-blue-600 text-white hover:bg-blue-700';
      case 'TERMINE':
        return 'bg-green-600 text-white hover:bg-green-700';
      case 'ABSENT':
        return 'bg-orange-100 text-orange-700 hover:bg-orange-200';
      case 'ANNULE':
        return 'bg-red-600 text-white hover:bg-red-700';
      default:
        return 'bg-gray-200 text-gray-700 hover:bg-gray-300';
    }
  };

  const getRdvConfirmationMeta = (commande, targetStatus) => {
    const numero = commande.numero || `RDV-${String(commande.id || '').toUpperCase()}`;
    const when = commande.dateDebut ? formatDateTime(commande.dateDebut) : null;

    switch (targetStatus) {
      case 'CONFIRME':
        return {
          title: 'Confirmer le rendez-vous',
          description: when
            ? `Confirmer le rendez-vous ${numero} prévu le ${when} ?`
            : `Confirmer le rendez-vous ${numero} ?`,
          tone: 'primary',
        };
      case 'TERMINE':
        return {
          title: 'Terminer le rendez-vous',
          description: 'Marquer ce rendez-vous comme terminé ?',
          tone: 'primary',
        };
      case 'ABSENT':
        return {
          title: 'Client absent',
          description: 'Marquer ce rendez-vous comme client absent ?',
          tone: 'primary',
        };
      case 'ANNULE':
        return {
          title: 'Annuler le rendez-vous',
          description: when
            ? `Annuler le rendez-vous ${numero} prévu le ${when} ?`
            : `Annuler le rendez-vous ${numero} ?`,
          tone: 'danger',
        };
      default:
        return {
          title: 'Mettre à jour le rendez-vous',
          description: `Confirmer le changement de statut (${targetStatus}) pour ${numero} ?`,
          tone: 'primary',
        };
    }
  };

  const updateCommandeStatus = async (commandeId, newStatus) => {
    try {
      setUpdatingStatusId(commandeId);
      await apiService.updateCommande(commandeId, { statut: newStatus });
      await fetchCommandes();
      if (showDetailModal && selectedCommande?.id === commandeId) {
        await refreshCommandeDetail(commandeId);
      } else {
        setSelectedCommande((prev) =>
          prev && prev.id === commandeId ? { ...prev, statut: newStatus } : prev
        );
      }
      window.dispatchEvent(new CustomEvent('clickorico:refresh-commandes-pending-count'));
    } catch (err) {
      alert(formatCommandeStatutUpdateError(err));
      console.error('Update status error:', err);
    } finally {
      setUpdatingStatusId(null);
    }
  };

  const updateRendezVousStatus = async (rendezVousId, newStatus, extra = {}) => {
    try {
      setUpdatingStatusId(rendezVousId);
      await apiService.updateRendezVous(rendezVousId, { statut: newStatus, ...extra });
      await fetchCommandes();
      setSelectedCommande((prev) =>
        prev && prev.id === rendezVousId ? { ...prev, statut: newStatus } : prev
      );
    } catch (err) {
      alert("Erreur lors de la mise à jour du rendez-vous");
      console.error('Update rendez-vous status error:', err);
    } finally {
      setUpdatingStatusId(null);
    }
  };

  const getNextStatus = (currentStatus, modeRetrait) => {
    const isClickCollect = modeRetrait === 'clickandcollect';

    switch (currentStatus) {
      case 'EN_ATTENTE':
      case 'CONFIRMEE':
        return 'EN_PREPARATION';
      case 'EN_PREPARATION':
        return isClickCollect ? 'PRETE' : 'EN_LIVRAISON';
      case 'PRETE':
        return isClickCollect ? 'LIVREE' : 'EN_LIVRAISON';
      case 'EN_LIVRAISON':
        return 'LIVREE';
      default:
        return null;
    }
  };

  const getNextStatusLabel = (currentStatus, modeRetrait) => {
    const isClickCollect = modeRetrait === 'clickandcollect';
    
    const labels = {
      'EN_ATTENTE': 'Confirmer',
      'CONFIRMEE': 'Mettre en préparation',
      'EN_PREPARATION': isClickCollect ? 'Marquer comme prête au retrait' : 'Passer en livraison',
      'PRETE': isClickCollect ? 'Marquer comme retirée' : 'Passer en livraison',
      'EN_LIVRAISON': 'Marquer comme livrée',
    };
    return labels[currentStatus] || null;
  };

  const getStatusLabel = (statut, modeRetrait) => {
    const isClickCollect = modeRetrait === 'clickandcollect';
    
    switch (statut) {
      case 'EN_ATTENTE': return 'EN ATTENTE';
      case 'CONFIRMEE': return 'CONFIRMÉE';
      case 'EN_PREPARATION': return 'EN PRÉPARATION';
      case 'PRETE': return isClickCollect ? 'PRÊTE AU RETRAIT' : 'PRÊTE';
      case 'EN_LIVRAISON': return isClickCollect ? 'PRÊTE AU RETRAIT' : 'EN LIVRAISON';
      case 'LIVREE': return isClickCollect ? 'RETIRÉE' : 'LIVRÉE';
      case 'ANNULEE': return 'ANNULÉE';
      default: return statut;
    }
  };

  const openStatusConfirmation = (commande, targetStatus, options = {}) => {
    if (!targetStatus) return;

    const label = getStatusLabel(targetStatus, commande.modeRetrait);
    const numero = commande.numero || `#${String(commande.id || '').toUpperCase()}`;

    setConfirmation({
      title: targetStatus === 'ANNULEE' ? 'Annuler la commande' : 'Changer le statut',
      description: `Confirmer le passage de la commande ${numero} vers le statut « ${label} » ?`,
      tone: targetStatus === 'ANNULEE' ? 'danger' : 'primary',
      onConfirm: async () => {
        await updateCommandeStatus(commande.id, targetStatus);
        if (options.afterConfirm) {
          await options.afterConfirm();
        }
      },
    });
  };

  const openRdvStatusConfirmation = (commande, targetStatus, options = {}) => {
    if (!targetStatus) return;

    if (targetStatus === 'TERMINE') {
      setTerminerModal({ open: true, commande });
      return;
    }

    const defaults = getRdvConfirmationMeta(commande, targetStatus);
    const merged = { ...defaults, ...options };

    setConfirmation({
      title: merged.title,
      description: merged.description,
      tone: merged.tone || (targetStatus === 'ANNULE' ? 'danger' : 'primary'),
      onConfirm: async () => {
        await updateRendezVousStatus(commande.id, targetStatus, merged.payload || {});
        if (merged.afterConfirm) {
          await merged.afterConfirm();
        }
      },
    });
  };

  const handleTerminerSuccess = async () => {
    const id = terminerModal.commande?.id;
    await fetchCommandes();
    if (id && showDetailModal && selectedCommande?.id === id) {
      try {
        const data = await apiService.getCommande(id);
        const full = data?.commande || data;
        if (full?.id) setSelectedCommande((prev) => (prev?.id === full.id ? { ...prev, ...full } : prev));
      } catch {
        /* ignore */
      }
    }
  };

  const resolveRdvCommercantId = (commande) =>
    commande?.commercantId ||
    commande?.commercant?.id ||
    userCommercantId ||
    null;

  const probeCaisseSessionForSelected = useCallback(async () => {
    const cid = resolveRdvCommercantId(selectedCommande);
    if (!cid || !showDetailModal) return;
    setEncaisserSoldeUi((prev) => ({ ...prev, probing: true, error: '' }));
    try {
      const data = await apiService.getCaisseSession(cid);
      setEncaisserSoldeUi((prev) => ({
        ...prev,
        hasSession: Boolean(data?.session?.id),
        probing: false,
      }));
    } catch {
      setEncaisserSoldeUi((prev) => ({
        ...prev,
        hasSession: false,
        probing: false,
      }));
    }
  }, [selectedCommande, showDetailModal, userCommercantId]);

  useEffect(() => {
    if (!showDetailModal || !selectedCommande || !isRendezVous(selectedCommande)) return;
    if (selectedCommande.statut !== 'TERMINE') return;
    const pay = getRdvPaymentView(selectedCommande);
    if (!(Number(pay?.amountDueLater) > 0)) return;
    probeCaisseSessionForSelected();
  }, [showDetailModal, selectedCommande, probeCaisseSessionForSelected]);

  const handleEncaisserSoldeOnly = async () => {
    if (!selectedCommande) return;
    const cid = resolveRdvCommercantId(selectedCommande);
    const pay = getRdvPaymentView(selectedCommande);
    const due = Number(pay?.amountDueLater || 0);
    if (!(due > 0)) return;

    setEncaisserSoldeUi((prev) => ({ ...prev, loading: true, error: '' }));
    try {
      const storageKey = `rdv-encaisser-solde:${selectedCommande.id}`;
      let idemKey;
      try {
        idemKey = sessionStorage.getItem(storageKey) || createCaisseIdempotencyKey();
        sessionStorage.setItem(storageKey, idemKey);
      } catch {
        idemKey = createCaisseIdempotencyKey();
      }

      await apiService.encaisserCaisse(
        {
          type: 'RENDEZ_VOUS',
          id: selectedCommande.id,
          methodePaiement: encaisserSoldeUi.methode,
          ...(cid ? { commercantId: cid } : {}),
        },
        { idempotencyKey: idemKey }
      );
      try {
        sessionStorage.removeItem(storageKey);
      } catch {
        /* ignore */
      }
      await fetchCommandes();
      try {
        const data = await apiService.getCommande(selectedCommande.id);
        const full = data?.commande || data;
        if (full?.id) setSelectedCommande((prev) => (prev?.id === full.id ? { ...prev, ...full } : prev));
      } catch {
        /* ignore */
      }
    } catch (err) {
      const msg =
        err?.data?.error?.message ||
        err?.data?.message ||
        err?.message ||
        "Impossible d'encaisser le solde";
      setEncaisserSoldeUi((prev) => ({ ...prev, error: msg }));
    } finally {
      setEncaisserSoldeUi((prev) => ({ ...prev, loading: false }));
    }
  };

  const getStatusColor = (statut) => {
    switch (statut) {
      case 'EN_ATTENTE':
        return 'bg-yellow-100 text-yellow-800';
      case 'CONFIRMEE':
        return 'bg-blue-100 text-blue-800';
      case 'EN_PREPARATION':
        return 'bg-orange-100 text-orange-800';
      case 'PRETE':
        return 'bg-purple-100 text-purple-800';
      case 'EN_LIVRAISON':
        return 'bg-indigo-100 text-indigo-800';
      case 'LIVREE':
        return 'bg-green-100 text-green-800';
      case 'ANNULEE':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (statut) => {
    switch (statut) {
      case 'EN_ATTENTE':
        return <Clock className="h-4 w-4" />;
      case 'CONFIRMEE':
      case 'EN_PREPARATION':
      case 'PRETE':
        return <CheckCircle className="h-4 w-4" />;
      case 'EN_LIVRAISON':
        return <Truck className="h-4 w-4" />;
      case 'LIVREE':
        return <CheckCircle className="h-4 w-4" />;
      case 'ANNULEE':
        return <XCircle className="h-4 w-4" />;
      default:
        return <Clock className="h-4 w-4" />;
    }
  };

  const renderPaiementCell = (commande) => (
    <MesCommandesListPaiementCell commande={commande} isPaiementStripe={isPaiementStripe} />
  );

  const mesCommandesRowContextDeps = {
    isRendezVous,
    getStatusDisplay,
    getTypeBadge,
    getNextStatus,
    getRdvActions,
    updatingStatusId,
    getRdvTotalAmount,
    getCommandeFinancier,
    getListPrincipalAmount,
    formatEuro,
    getNextStatusLabel,
    getRdvConfirmationMeta,
    getRdvActionIcon,
  };

  const mesCommandesRowHandlers = {
    onOpenDetail: openCommandeDetail,
    onProductStatus: (commande, nextStatus) => openStatusConfirmation(commande, nextStatus),
    onProductCancel: (commande) => openStatusConfirmation(commande, 'ANNULEE'),
    onRdvAction: (commande, status, meta) => openRdvStatusConfirmation(commande, status, meta),
    renderPaiementCell,
  };

  const platformShowProductSections = filters.type !== 'RENDEZ_VOUS';
  const platformShowRdvSections = filters.type !== 'COMMANDE';

  const orderDetailModalCommonProps = {
    onClose: closeDetailModal,
    isUpdating: updatingStatusId === selectedCommande?.id,
    getNextStatus,
    getStatusDisplay,
    getNextStatusLabel,
    openStatusConfirmation,
    openRefundModal,
    hasStripePaymentIntent,
    getCommandeSoldeRemboursable,
    isCommandePaiementEnLigne,
    isRendezVous,
    reprintLoading,
    onReprintTicket: handleReprintTicket,
    refundLoading,
    factureLoading,
    onDownloadFacture: handleDownloadFacture,
    avoirLoadingId,
    onDownloadAvoir: handleDownloadAvoir,
    livreursDisponibles,
    livreurHistorique,
    selectedLivreurId,
    onSelectedLivreurIdChange: setSelectedLivreurId,
    assigningLivreur,
    onAssignerLivreur: handleAssignerLivreur,
    expeditionCapabilities,
    generatingShippingOrder: false,
    onOpenGenerateExpeditionConfirmation: noop,
    expeditionLabelUi,
    expeditionLabelPolling: false,
    onRefreshExpeditionLabelStatus: noop,
    onDownloadExpeditionLabel: noop,
    merchantHandoffSubmitting: false,
    onOpenExpeditionHandoffConfirmation: noop,
    expeditionTrackingSyncing: false,
    onSyncExpeditionTracking: noop,
    poidsComplementUrl,
    poidsDrafts,
    onPoidsDraftsChange: setPoidsDrafts,
    indisponibleMessage,
    markingIndisponible,
    onOpenLigneIndisponibleConfirmation: noop,
    statutsLigneIndisponible: STATUTS_LIGNE_INDISPONIBLE,
    parseSavReference,
    formatDateTime,
    poidsError,
    poidsMessage,
    savingPoids,
    onSavePoidsAjustement: handleSavePoidsAjustement,
  };

  const isRdvFilter = filters.type === 'RENDEZ_VOUS';
  const statusOptions = isRdvFilter
    ? [
        { value: '', label: 'Tous' },
        { value: 'EN_ATTENTE', label: 'En attente' },
        { value: 'CONFIRME', label: 'Confirmés' },
        { value: 'TERMINE', label: 'Terminés' },
        { value: 'ABSENT', label: 'Absents' },
        { value: 'ANNULE', label: 'Annulés' },
      ]
    : [
        { value: '', label: 'Tous' },
        { value: 'EN_ATTENTE', label: 'En attente' },
        { value: 'CONFIRMEE', label: 'Confirmées' },
        { value: 'EN_PREPARATION', label: 'En préparation' },
        { value: 'PRETE', label: 'Prête' },
        { value: 'EN_LIVRAISON', label: 'En livraison' },
        { value: 'LIVREE', label: 'Livrée' },
        { value: 'ANNULEE', label: 'Annulée' },
      ];

  if (loading && commandes.length === 0) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Commandes</h1>
          <p className="text-gray-600">Gestion des commandes de la plateforme</p>
        </div>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      </div>
    );
  }

  if (error && commandes.length === 0) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Commandes</h1>
          <p className="text-red-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
    <div>
        <h1 className="text-2xl font-bold text-gray-900">Commandes</h1>
        <p className="text-gray-600">Gestion des commandes de la plateforme</p>
      </div>

      <div className="card">
        <div className="px-6 pt-4 pb-2 space-y-3 md:space-y-0 md:flex md:items-end md:justify-between md:flex-wrap md:gap-3">
          <div className="flex-1 min-w-[220px]">
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">
              Recherche globale
            </label>
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Numéro, client, téléphone, commerçant, produit..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">
              Type
            </label>
            <select
              value={filters.type}
              onChange={(e) => handleSelectFilter('type', e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="">Commandes & rendez-vous</option>
              <option value="COMMANDE">Commandes uniquement</option>
              <option value="RENDEZ_VOUS">Rendez-vous uniquement</option>
            </select>
          </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">
                Statut
              </label>
              <select
              value={filters.statut}
              onChange={(e) => handleSelectFilter('statut', e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
              {statusOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
              </select>
            </div>
          {!isRdvFilter && (
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">
                Mode
              </label>
              <select
                value={filters.modeRetrait}
                onChange={(e) => handleSelectFilter('modeRetrait', e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">Tous</option>
                <option value="clickandcollect">Click & Collect</option>
                <option value="livraison">Livraison</option>
              </select>
            </div>
          )}
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">
                Du
              </label>
              <input
                type="date"
                value={filters.dateMin}
                onChange={(e) => handleDateFilter('dateMin', e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">
                Au
              </label>
              <input
                type="date"
                value={filters.dateMax}
                onChange={(e) => handleDateFilter('dateMax', e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            <button
              type="button"
              onClick={resetFilters}
              className="px-3 py-2 text-sm font-medium text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
            >
              Réinitialiser
            </button>
          </div>
        </div>

        {commandes.length > 0 ? (
          <PlatformCommandesMaquetteLists
            commandes={commandes}
            isRendezVous={isRendezVous}
            isCommandeEnCours={isCommandeEnCours}
            sectionsExpanded={sectionsExpanded}
            onToggleSection={(key) =>
              setSectionsExpanded((prev) => ({ ...prev, [key]: !prev[key] }))
            }
            newCommandes={newCommandes}
            rowContextDeps={mesCommandesRowContextDeps}
            rowHandlers={mesCommandesRowHandlers}
            showProductSections={platformShowProductSections}
            showRdvSections={platformShowRdvSections}
          />
        ) : (
          <div className="text-center py-12">
            <div className="mx-auto h-12 w-12 text-gray-400">
              <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
            <h3 className="mt-2 text-sm font-medium text-gray-900">Aucune commande</h3>
            <p className="mt-1 text-sm text-gray-500">
              Il n'y a pas encore de commandes sur la plateforme.
            </p>
          </div>
        )}
      </div>

      {showDetailModal && selectedCommande && isRendezVous(selectedCommande) ? (
        <MesCommandesRdvDetailModal
          commande={selectedCommande}
          statusDisplay={getStatusDisplay(selectedCommande)}
          isUpdating={updatingStatusId === selectedCommande.id}
          rdvActions={getRdvActions(selectedCommande.statut)}
          encaisserSoldeUi={{
            ...encaisserSoldeUi,
            onMethodeChange: (methode) =>
              setEncaisserSoldeUi((prev) => ({ ...prev, methode })),
          }}
          onClose={closeDetailModal}
          onRdvAction={(commande, status, meta) =>
            openRdvStatusConfirmation(commande, status, meta)
          }
          onOpenRefund={openRefundModal}
          onEncaisserSolde={handleEncaisserSoldeOnly}
          getRdvActionButtonClass={getRdvActionButtonClass}
          getRdvActionIcon={getRdvActionIcon}
          getRdvConfirmationMeta={getRdvConfirmationMeta}
          refundLoading={refundLoading}
        />
      ) : null}
      {showDetailModal && selectedCommande && !isRendezVous(selectedCommande) ? (
        <MesCommandesOrderDetailModal
          commande={selectedCommande}
          {...orderDetailModalCommonProps}
        />
      ) : null}

            {/* Toast Manager pour les notifications */}
      <CommandeToastManager
        notifications={notifications}
        onDismiss={(eventKey) => {
          if (!eventKey) return;
          markCommandeToastDismissed(eventKey);
          setNotifications((prev) =>
            prev.filter((entry) => getNotificationEventKey(entry) !== eventKey),
          );
        }}
        onView={(notification) => {
          const commandeId = notification?.data?.commandeId || notification?.data?.id;
          if (!commandeId) return;
          void (async () => {
            audioService.stopRepeat();
            await fetchCommandes({ silent: true });
            try {
              const data = await apiService.getCommande(commandeId);
              const full = data?.commande || data;
              if (full?.id) await openCommandeDetail(full);
            } catch (err) {
              console.error('Erreur ouverture commande depuis notification:', err);
            }
          })();
        }}
      />

      <ConfirmDialog
        isOpen={!!confirmation}
        onCancel={() => setConfirmation(null)}
        onConfirm={async () => {
          if (!confirmation?.onConfirm) return false;
          try {
            await confirmation.onConfirm();
            setConfirmation(null);
            return true;
          } catch (err) {
            console.error(err);
            alert('Erreur lors de la mise à jour. Veuillez réessayer.');
            return false;
          }
        }}
        title={confirmation?.title}
        description={confirmation?.description}
        tone={confirmation?.tone}
      />

      <RdvTerminerModal
        open={terminerModal.open}
        commande={terminerModal.commande}
        commercantId={resolveRdvCommercantId(terminerModal.commande)}
        onClose={() => setTerminerModal({ open: false, commande: null })}
        onSuccess={handleTerminerSuccess}
        formatEuro={formatEuro}
      />
    </div>
  );
};

export default Commandes;
